import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Lock } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/admin");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 relative overflow-hidden">
      <div className="grain" />
      <div className="absolute top-0 left-0 w-full h-1 bg-primary" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md relative z-10"
      >
        <Link to="/" data-testid="back-home" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>

        <div className="border border-border bg-card p-8 lg:p-10">
          <div className="h-12 w-12 bg-primary flex items-center justify-center mb-6">
            <Lock className="w-5 h-5 text-primary-foreground" />
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Admin Access</h1>
          <p className="text-muted-foreground text-sm mt-2">Sign in to manage inquiries.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-mono text-xs tracking-widest uppercase text-muted-foreground">Email</Label>
              <Input id="email" type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@technostamping.com" className="rounded-none bg-background border-border h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="font-mono text-xs tracking-widest uppercase text-muted-foreground">Password</Label>
              <Input id="password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="rounded-none bg-background border-border h-12" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-none h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in...</> : "Sign In"}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
